import { PersonaMapping, PersonaState } from '../models/AlmePersona';
import { AlmeIvaTypeProp } from "../models/AlmeIvaTypeMapping";
import personaMappingJson from '../personas/personaMapping.json';

import { Theme, ThemeOptions } from '@material-ui/core';
import { createMuiTheme, responsiveFontSizes } from '@material-ui/core/styles';
import defaultLogger from '../Logger';
import deepmerge from 'deepmerge';
import baseTheme from '../personas/baseThemeOverrides';
import { createIntlCache, createIntl, IntlShape } from 'react-intl';
import { OptionalIntlConfig } from 'react-intl/src/components/provider';
import defaultTranslation from "../personas/mainPersona/translations/languages.json";

export function getAccessKeyFromQuery(): string | undefined {
    const accessKeyParamName = 'almeAccessKey';
    if (typeof URLSearchParams !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const accessKeyParam = urlParams.get(accessKeyParamName);
        return accessKeyParam || undefined;
    }
    else {
        // IE Fallback
        const url = window.location.href;
        const regex = new RegExp('[?&]' + accessKeyParamName + '(=([^&#]*)|&|#|$)');
        const results = regex.exec(url);
        if (!results) {
            return;
        }
        if (!results[2]) {
            return;
        }
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }
}

export function getAccessKey(ivaType?: AlmeIvaTypeProp): string | undefined {
    if (ivaType && ivaType === 'popup') {
        return getAccessKeyFromQuery();
    }
    else {
        return undefined;
    }
}

export function createTheme(themeStack?: ThemeOptions[]): Theme {
    const mergedTheme = themeStack ? deepmerge.all([baseTheme, ...(themeStack.filter(e => e != null))]) : baseTheme;
    let theme = createMuiTheme(mergedTheme);
    try {
        theme = responsiveFontSizes(theme);
    } catch (ex) {
        defaultLogger.log('failed to set response font sizes\n', ex);
    }
    return theme;
}

export interface DynamicPersonaModule {
    default: PersonaState;
}

export async function getPersonaFromMapping(newKey: string, personaState: PersonaState, personaMapped?: React.MutableRefObject<boolean>): Promise<PersonaState> {
    let persona = {
        ...personaState,
    } as PersonaState;
    for (let i = 0; i < personaMappingJson.length; i++) {
        const personaMapping = personaMappingJson[i] as PersonaMapping;
        if (personaMapping.verintAccessKey.toLowerCase() === newKey.toLowerCase()) {
            const newPersona = (await import(`../personas/${personaMapping.location}`) as DynamicPersonaModule).default;
            if (personaMapped) {
                personaMapped.current = true;
            }
            let language = document.documentElement.lang;
            language = normalizeLanguageTag(language);
            const languageFile = (await import(`../personas/${personaMapping.languageFile}`) as Record<string, Record<string, string>>)[language];
            persona = {
                ...newPersona,
                verintAccessKey: { current: newKey.toLowerCase() },
                currentLanguageFile: languageFile,
                currentLanguage: language,
            };
        }
    }
    return persona;
}

let currentIntlConfig: OptionalIntlConfig = { locale: 'en-US', messages: (defaultTranslation as Record<string, Record<string, string>>)["en-US"] };

export function getCurrentIntlConfig(): OptionalIntlConfig {
    return currentIntlConfig;
}

export function setIntlConfig(currentLanguage: string, currentLanguageFile: Record<string, string>): IntlShape {
    const cache = createIntlCache();

    const intlConfig = { locale: currentLanguage, messages: currentLanguageFile } as OptionalIntlConfig;
    currentIntlConfig = intlConfig;

    return createIntl(
        intlConfig,
        cache,
    );
}

export function normalizeLanguageTag(language: string): string {
    switch (language) {
        case 'es-ES':
        case 'es-US':
        case 'es-us':
        case 'es-es':
        case 'es':
            return 'es-ES';
        case 'en-US':
        case 'en':
        case 'en-us':
            return 'en-US';
        default:
            return 'en-US';
    }
}

export const defaultAccessKey = "00000000-0000-0000-0000-000000000000";
export const muiThemeAccessKey = "ffffffff-ffff-ffff-ffff-ffffffffffff";
export const C4CKey = "2592608f-208d-43fe-b6a9-5e5f94a498dd";
export const OzempicKey = "298578a3-1ffc-41d5-8b6b-9633edd0d575";
export const VictozaKey = "a082ce3c-8bc3-4914-afdc-557a58ee1f7a";
export const OzempicConsumerKey = "75e9c154-e580-4705-bc7f-422ae73e54d0";
export const RybelsusConsumerKey = "9bf8112d-e721-4f7e-8f8b-ecd52368a7da";
export const TresibaConsumerKey = "54db5e74-8bee-466c-8bd8-c590623187a6";
export const NovocareDiabetesEducationKey = "8295fc13-7040-44cf-ba3a-a85f606dc253";
export const FiaspKey = "e656a44e-b4d1-4b81-a213-a3503dc69a85";
export const XultophyKey = "86752a2e-c9e7-454f-b041-97d4293b7cb8";
export const LevemirKey = "ceacac47-315a-42f7-beb7-6b3b5205442c";
export const NovologKey = "2fe48db3-4669-4af7-8b48-04f20baa633e";
export const NovologMixKey = "02d125f0-a753-4321-b873-8dc8a8d5a109";
export const MyNovoInsulinHomepageKey = "b2fd990b-fd6f-42bd-b44a-2b8e93593ff4";

